import React, { useEffect, useRef, useState } from 'react';
import { propagateQuerytoExternalLinks, trackButtonActivity } from 'src/functions/propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';

import { StyledMenuExpandableToggle, StyledMenuLink } from './MenuLink.styles';
import menuLinkClickStatsigTrack from 'src/functions/menuClickLinkStatsigTrack';
import { IMenuLink } from 'src/molecules/Shared/2023_Q4/Header/Header.links';
import Chevron from 'src/images/2023_Q4/Chevron';
import { createPortal } from 'react-dom';
import { useExpandedMenuController } from 'src/hooks/useExpandedMenuController';

interface MenuLinkProps extends IMenuLink {
  readonly locationPathname: string;
  readonly fadeIn?: boolean;
}

const MenuLink = (props: MenuLinkProps): JSX.Element => {
  const { href, text, locationPathname } = props;
  const { currentExpanded, toggleExpandedMenu } = useExpandedMenuController();

  const buttonRef = useRef<HTMLAnchorElement | any>();
  const [queryPropagatedUrl, updateHref] = useState(href);

  const expandableMenuOpen = currentExpanded === props.text;

  useEffect(() => {
    windowIsLoaded().then(() => {
      updateHref(propagateQuerytoExternalLinks(href));
    });
  }, []);

  const trackLinkOnCLick = () => {
    menuLinkClickStatsigTrack(props.trackingSlug);
    trackButtonActivity(buttonRef.current);
    return true; // To proceed with anchor link event
  };

  const onExpandableMenuToggle = () => {
    if (!expandableMenuOpen) {
      // Track only opening the expandable menu
      trackLinkOnCLick();
    }

    toggleExpandedMenu(props.text);
  };

  const onExpandableMenuClose = () => {
    // Close the expandable menu
    toggleExpandedMenu(props.text, false);
  };

  const isActivePath = locationPathname === href;

  if (props.useExpandableMenu && props.expandableMenu) {
    // Expandable menus are #header children keep as fragment to avoid toggle click event propagation
    const expandableRenderTarget = typeof document !== 'undefined' ? document.getElementById('header') : null;
    return (
      <>
        <StyledMenuExpandableToggle
          ref={buttonRef}
          className={`styled-menu-link ${isActivePath ? 'active' : ''}`}
          $fadeIn={props.fadeIn}
          onClick={onExpandableMenuToggle}
        >
          <span>{text}</span>
          <Chevron
            width={18}
            orientation={expandableMenuOpen ? 'up' : 'down'}
          />
        </StyledMenuExpandableToggle>
        {expandableRenderTarget &&
          createPortal(
            <props.expandableMenu
              onClose={onExpandableMenuClose}
              isOpen={expandableMenuOpen}
            />,
            expandableRenderTarget
          )}
      </>
    );
  }

  // Default Link behavior
  return (
    <StyledMenuLink
      ref={buttonRef}
      to={queryPropagatedUrl}
      className={`styled-menu-link ${isActivePath ? 'active' : ''}`}
      onClick={trackLinkOnCLick}
      $fadeIn={props.fadeIn}
    >
      {text}
    </StyledMenuLink>
  );
};

export default MenuLink;
